"use client"

/**
 * react-router-dom -> next/navigation compatibility shim.
 *
 * Existing pages import hooks/components from "@/lib/router-compat"; after the
 * Next.js migration those imports are rewritten to this module. Each export
 * mirrors the RRD API surface that the app actually uses so individual call
 * sites do not have to change.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  type AnchorHTMLAttributes,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
} from "react"
import NextLink from "next/link"
import {
  usePathname,
  useRouter,
  useSearchParams as useNextSearchParams,
  useParams as useNextParams,
  redirect as nextRedirect,
} from "next/navigation"

export type NavigateOptions = {
  replace?: boolean
  state?: unknown
}

export type To = string | number

/* ------------------------------------------------------------------ */
/* useNavigate                                                         */
/* ------------------------------------------------------------------ */

export function useNavigate() {
  const router = useRouter()

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      if (typeof to === "number") {
        if (to < 0) router.back()
        else if (to > 0) router.forward()
        return
      }
      if (options?.replace) {
        router.replace(to)
      } else {
        router.push(to)
      }
    },
    [router]
  )
}

/* ------------------------------------------------------------------ */
/* useLocation                                                         */
/* ------------------------------------------------------------------ */

export type Location = {
  pathname: string
  search: string
  hash: string
  state: any
  key: string
}

export function useLocation(): Location {
  const pathname = usePathname() ?? "/"
  const searchParams = useNextSearchParams()
  const search = useMemo(() => {
    const str = searchParams?.toString() ?? ""
    return str.length > 0 ? `?${str}` : ""
  }, [searchParams])

  return useMemo<Location>(
    () => ({
      pathname,
      search,
      hash: typeof window !== "undefined" ? window.location.hash : "",
      state:
        (typeof window !== "undefined" ? window.history.state : null) ?? null,
      key: "default",
    }),
    [pathname, search]
  )
}

/* ------------------------------------------------------------------ */
/* useParams                                                           */
/* ------------------------------------------------------------------ */

export function useParams<
  P extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>(): P {
  const params = useNextParams() as Record<
    string,
    string | string[] | undefined
  >
  const out: Record<string, string | undefined> = {}
  if (params) {
    for (const key of Object.keys(params)) {
      const value = params[key]
      out[key] = Array.isArray(value) ? value[0] : value
    }
  }
  return out as P
}

/* ------------------------------------------------------------------ */
/* useSearchParams                                                     */
/* ------------------------------------------------------------------ */
/**
 * RRD's `useSearchParams` returns a tuple `[URLSearchParams, setSearchParams]`.
 * The Next.js equivalent is read-only, so the setter pushes a new URL onto
 * the router. Behaviour matches the common consumer pattern.
 */

export function useSearchParams(): [
  URLSearchParams,
  (
    next:
      | URLSearchParams
      | Record<string, string | number | undefined | null>
      | ((prev: URLSearchParams) => URLSearchParams),
    options?: { replace?: boolean }
  ) => void,
] {
  const router = useRouter()
  const pathname = usePathname() ?? "/"
  const raw = useNextSearchParams()

  const current = useMemo(
    () => new URLSearchParams(raw?.toString() ?? ""),
    [raw]
  )

  const setSearchParams = useCallback(
    (
      next:
        | URLSearchParams
        | Record<string, string | number | undefined | null>
        | ((prev: URLSearchParams) => URLSearchParams),
      options?: { replace?: boolean }
    ) => {
      let params: URLSearchParams
      if (typeof next === "function") {
        params = next(new URLSearchParams(current.toString()))
      } else if (next instanceof URLSearchParams) {
        params = next
      } else {
        params = new URLSearchParams()
        for (const [k, v] of Object.entries(next)) {
          if (v === undefined || v === null) continue
          params.set(k, String(v))
        }
      }
      const queryString = params.toString()
      const url = queryString ? `${pathname}?${queryString}` : pathname
      if (options?.replace) {
        router.replace(url)
      } else {
        router.push(url)
      }
    },
    [router, pathname, current]
  )

  return [current, setSearchParams]
}

/* ------------------------------------------------------------------ */
/* Link / NavLink                                                      */
/* ------------------------------------------------------------------ */

type RRDLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  to?: string
  href?: string
  replace?: boolean
  state?: unknown
  prefetch?: boolean
  children?: ReactNode
}

export const Link = forwardRef<HTMLAnchorElement, RRDLinkProps>(function Link(
  { to, href, replace, state: _state, prefetch, ...rest },
  ref
) {
  const target = href ?? to ?? "#"
  const nextProps: ComponentProps<typeof NextLink> = {
    href: target,
    replace,
    prefetch,
  }
  return <NextLink ref={ref} {...nextProps} {...rest} />
})

type NavLinkRenderProps = {
  isActive: boolean
  isPending: boolean
  isTransitioning: boolean
}

type NavLinkProps = Omit<RRDLinkProps, "children" | "className" | "style"> & {
  end?: boolean
  caseSensitive?: boolean
  children?: ReactNode | ((state: NavLinkRenderProps) => ReactNode)
  className?: string | ((state: NavLinkRenderProps) => string)
  style?:
    | React.CSSProperties
    | ((state: NavLinkRenderProps) => React.CSSProperties | undefined)
}

export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  function NavLink(
    { to, href, end, caseSensitive, className, style, children, ...rest },
    ref
  ) {
    const target = href ?? to ?? "#"
    const pathname = usePathname() ?? "/"

    const normalize = (s: string) => (caseSensitive ? s : s.toLowerCase())

    const targetPath = target.split("?")[0].split("#")[0]
    const cur = normalize(pathname)
    const tgt = normalize(targetPath)

    const isActive = end
      ? cur === tgt
      : cur === tgt || cur.startsWith(`${tgt}/`)

    const state: NavLinkRenderProps = {
      isActive,
      isPending: false,
      isTransitioning: false,
    }

    const computedClassName =
      typeof className === "function" ? className(state) : className
    const computedStyle = typeof style === "function" ? style(state) : style
    const computedChildren =
      typeof children === "function" ? children(state) : children

    return (
      <NextLink
        ref={ref}
        href={target}
        className={computedClassName}
        style={computedStyle}
        {...rest}
      >
        {computedChildren}
      </NextLink>
    )
  }
)

/* ------------------------------------------------------------------ */
/* Navigate                                                            */
/* ------------------------------------------------------------------ */

export function Navigate({
  to,
  replace,
}: {
  to: string
  replace?: boolean
  state?: unknown
}) {
  const router = useRouter()

  useEffect(() => {
    if (replace) router.replace(to)
    else router.push(to)
  }, [router, to, replace])

  return null
}

/* ------------------------------------------------------------------ */
/* Misc helpers                                                        */
/* ------------------------------------------------------------------ */

export function redirect(to: string): never {
  return nextRedirect(to) as never
}

/**
 * In the RRD codebase, `<Outlet />` is used inside layout components to
 * render the nested route. With App Router, layouts receive `children`
 * directly. We export a no-op `Outlet` so any straggler imports compile;
 * layouts still using <Outlet /> have been refactored to accept children.
 */
export function Outlet({ children }: { children?: ReactNode } = {}) {
  return <>{children ?? null}</>
}

export {
  // Pass-through re-exports for the common nav helpers
  useRouter,
  usePathname,
}

/* generatePath helper kept for parity with RRD usage */
export function generatePath(
  path: string,
  params: Record<string, string | number | undefined> = {}
): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const v = params[key]
    if (v === undefined) return ""
    return String(v)
  })
}

/* createSearchParams helper */
export function createSearchParams(
  init?: Record<string, string | number | (string | number)[]> | URLSearchParams
): URLSearchParams {
  if (init instanceof URLSearchParams) return init
  const out = new URLSearchParams()
  if (!init) return out
  for (const [k, v] of Object.entries(init)) {
    if (Array.isArray(v)) {
      for (const item of v) out.append(k, String(item))
    } else {
      out.set(k, String(v))
    }
  }
  return out
}
